import { Box, Heading, Table } from '@hyphen/hyphen-components';

/**
 * Renders the Markdown this app generates.
 *
 * Deliberately not a Markdown library. The only Markdown rendered here is the
 * Markdown this app writes, in internal/render, which uses three heading levels,
 * block quotes, tables, a horizontal rule, and bold, italic and code inline —
 * and nothing else. A general parser would be a dependency carrying a parser for
 * a language we do not use in order to read a document we wrote ourselves.
 *
 * The trade is real and worth naming: if the generator starts emitting something
 * new, it renders as the plain text it is rather than as the thing it meant. So
 * the generator and this file are a pair, and a new construct in one needs the
 * other.
 */
export function Markdown({ children }) {
  return <Box gap="lg">{blocks(String(children ?? ''))}</Box>;
}

function blocks(text) {
  const lines = text.split('\n');
  const out = [];
  let paragraph = [];

  const flush = () => {
    if (paragraph.length === 0) return;
    out.push(
      <Box as="p" key={`p${out.length}`} maxWidth="52rem">
        {inline(paragraph.join(' '))}
      </Box>,
    );
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.trim() === '') {
      flush();
      continue;
    }

    if (line.startsWith('### ')) {
      flush();
      out.push(<Heading as="h3" size="sm" key={`h${i}`}>{inline(line.slice(4))}</Heading>);
      continue;
    }
    if (line.startsWith('## ')) {
      flush();
      out.push(<Heading as="h2" size="md" key={`h${i}`}>{inline(line.slice(3))}</Heading>);
      continue;
    }
    if (line.startsWith('# ')) {
      flush();
      out.push(<Heading as="h1" size="lg" key={`h${i}`}>{inline(line.slice(2))}</Heading>);
      continue;
    }

    if (line.startsWith('> ')) {
      flush();
      const quoted = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoted.push(lines[i].slice(2));
        i += 1;
      }
      i -= 1;
      out.push(
        <Box
          key={`q${i}`}
          as="blockquote"
          padding="lg"
          background="secondary"
          borderWidth="0 0 0 md"
          borderColor="info"
          maxWidth="52rem"
          style={{ margin: 0 }}
        >
          {inline(quoted.join(' '))}
        </Box>,
      );
      continue;
    }

    if (line.trim() === '---') {
      flush();
      out.push(<Box key={`r${i}`} borderWidth="sm 0 0 0" borderColor="subtle" />);
      continue;
    }

    if (line.startsWith('|')) {
      flush();
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(cells(lines[i]));
        i += 1;
      }
      i -= 1;
      out.push(table(rows, i));
      continue;
    }

    paragraph.push(line);
  }
  flush();
  return out;
}

/** Splits a table row, dropping the empty cells either side of the outer pipes. */
function cells(line) {
  return line.split('|').slice(1, -1).map((cell) => cell.trim());
}

/** A separator row — |---|---| — which carries no content. */
const isSeparator = (row) => row.every((cell) => /^:?-{3,}:?$/.test(cell));

function table(rows, key) {
  const [head, ...rest] = rows;
  if (!head) return null;
  const body = rest.filter((row) => !isSeparator(row));

  const columns = head.map((heading, index) => ({
    heading,
    dataKey: `c${index}`,
    render: (cell) => inline(cell ?? ''),
  }));

  return (
    <Table
      key={`t${key}`}
      rowKey="key"
      isScrollable={{ x: true }}
      columns={columns}
      rows={body.map((row, index) => {
        const record = { key: `${key}:${index}` };
        row.forEach((cell, column) => {
          record[`c${column}`] = cell;
        });
        return record;
      })}
    />
  );
}

// **bold**, `code`, _italic_, and a bare URL. Matched in one pass so that a
// pattern inside another — a URL in a code span, most often — is not rewritten
// from the inside out.
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|_[^_]+_|https?:\/\/\S+)/g;

function inline(text) {
  const parts = String(text).split(INLINE);
  return parts.map((part, index) => {
    const key = `${index}:${part}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <Box as="code" key={key} fontSize="sm" style={{ wordBreak: 'break-all' }}>
          {part.slice(1, -1)}
        </Box>
      );
    }
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={key} href={part} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
          {part}
        </a>
      );
    }
    return part;
  });
}
